<template>
  <CForm @submit="submit">
    <CModal :title="$t('partsModal.title')" icon="bolt" @close="close">
      <template #body>
        <CField v-model="formData.location" type="text" :label="$t('partsModal.location')" :validators="locationValidator"/>

        <CCard v-if="partItems.length > 1" class="u-p-1 u-pl-4">
          <CFlex>
            <CFill><h3>{{ $t('partsModal.all') }}</h3></CFill>
            <CBatchStockStatusButton :items="formData.items"/>
            <CBatchPickingStatusButton :items="formData.items"/>
          </CFlex>
        </CCard>

        <template v-if="partItems.length > 0">
          <CLinkedJobParts v-for="job in context.jobs" :job :context v-model="formData" class="u-my-3"/>

          <CSectionCard v-if="unlinkedItems.length > 0" icon="link-broken" :title="$t('partsModal.unassigned')">
            <CJobPart v-for="item in unlinkedItems" :item :context v-model="formData"/>
          </CSectionCard>
        </template>

        <CRemark v-else>{{ $t('partsModal.empty') }}</CRemark>
      </template>
    </CModal>
  </CForm>
</template>

<script setup lang="ts">
  import { computed, reactive } from 'vue';
  import CModal from '@/general/components/CModal.vue';
  import CCard from '@/shared/components/CCard.vue';
  import CFill from '@/shared/components/CFill.vue';
  import CFlex from '@/shared/components/CFlex.vue';
  import CRemark from '@/shared/components/form/CRemark.vue';
  import CField from '@/shared/components/form/CField.vue';
  import CForm from '@/shared/components/form/CForm.vue';
  import CSectionCard from '@/shared/components/section/CSectionCard.vue';
  import { maxLength } from '@/shared/utils/form/validation';
  import CBatchPickingStatusButton from '../components/CBatchPickingStatusButton.vue';
  import CBatchStockStatusButton from '../components/CBatchStockStatusButton.vue';
  import CJobPart from '../components/item/CJobPart.vue';
  import CLinkedJobParts from '../components/item/CLinkedJobParts.vue';
  import JobContext from '../models/JobContext';
  import { JobPartsData } from '../models/JobItem';
  import jobItemService from '../services/JobItemService';

  const emit = defineEmits<{
    (e: 'close'): void
  }>();

  const props = defineProps<{
    context: JobContext;
  }>();

  const partItems = computed(() => props.context.getActivePartItems());

  const unlinkedItems = computed(() => partItems.value.filter(i => i.jobId === null));

  const formData = reactive({
    location: props.context.dossier.partsPickingLocation ?? '',
    items: partItems.value.map(item => ({
      id: item.id,
      stockStatus: item.stockStatus,
      pickingStatus: item.pickingStatus,
    })),
  }) as JobPartsData;

  const locationValidator = { maxLength: maxLength(256) };

  async function submit() {
    await jobItemService.updatePartStatuses(formData, props.context);
    close();
  }

  function close() {
    emit('close');
  }
</script>